import { AxiosError } from 'axios';
import { router } from '@Routes/__root.tsx';

import { Icons } from '@Components/icon/Icon.tsx';
import { useBoundStore } from '@Store/useBoundStore.ts';
import { ErrorModalTypes } from '@Types/error.types.ts';
import { routesModel } from '@Router/router.consts.ts';
import { RoutesMap } from '@Router/router.types.ts';
import {
  reportErrorOccurredDuringOnboarding,
  reportFormErrorShown,
  reportWillViewExpireToken,
} from '@Services/analytics/GeneralAnalaytics.tsx';
import { AuthErrorMap } from '@Services/errors/auth.errors.ts';
import {
  ObservabilityErrorSeverity,
  reportError,
} from '@Services/observability/initObservability.ts';
import { MAIN_INCOME_SOURCE } from '@Types/consts';
import { GeneralErrorMap } from './general.errors.ts';
import { ErrorsMapList, getCombinedErrorsObjectMap } from './errorsMap.ts';
import type { ServerErrorResponse } from '@Types/index.ts';

// Function to clear access tokens from both customer and auth onboarding slices
const clearAccessTokens = () => {
  const setCustomerAccessToken = useBoundStore.getState().setCustomerAccessToken;
  const setAuthUserAccessToken = useBoundStore.getState().setAuthUserAccessToken;

  // Clear customer access token
  setCustomerAccessToken('');

  // Clear auth user access token
  setAuthUserAccessToken('');

  useBoundStore.getState().setOnboardingAuthenticated(false);
};

export const handleServerError = (
  errorCode: number,
  localizedErrorMessage: string,
  rawHttpStatus: number
) => {
  const {
    customerId,
    customerData: {
      customerBusinessData: { mainIncomeSource },
    },
  } = useBoundStore.getState().getCustomerData();
  const setFormFieldError = useBoundStore.getState().errorSlice.setFormFieldError;
  const setErrorModal = useBoundStore.getState().errorSlice.setErrorModal;
  const isFromLiliApp = useBoundStore.getState().onboardingSlice.isFromLiliApp;
  const routerData = useBoundStore.getState().onboardingSlice.routerData;
  const isUsFlow = useBoundStore.getState().getIsUsFlow();
  const isUboValidForNonUsFlow = useBoundStore.getState().getIsUboValidForNonUsFlow();
  const shouldActAsUsFlow =
    isUsFlow && !isUboValidForNonUsFlow && mainIncomeSource !== MAIN_INCOME_SOURCE.NON_PROFIT;

  reportErrorOccurredDuringOnboarding(
    errorCode,
    localizedErrorMessage,
    routerData.prevRoute,
    isFromLiliApp
  );

  // Clear access tokens when 401 error occurs
  if (errorCode === AuthErrorMap.TOKEN_EXPIRED.errorCode) {
    clearAccessTokens();
    //Monitor USER_INCORRECT_STATE error
  } else if (errorCode === GeneralErrorMap.INCORRECT_STATE.errorCode) {
    reportError({
      severity: ObservabilityErrorSeverity.ERROR,
      metadata: {
        name: 'USER_INCORRECT_STATE',
        message: `${localizedErrorMessage} - customerId: ${customerId}`,
      },
      groupingHash: 'user-incorrect-state',
    });
  }

  const isErrorMapped = (errorCodeTemp: number) =>
    ErrorsMapList.some((error) => error.errorCode === errorCodeTemp);

  if (!isErrorMapped(errorCode)) {
    setErrorModal({
      error: 'An unknown error occurred',
      modalProps: {
        title: 'An unknown error occurred',
        content: 'Please try again later',
        icon: Icons.Oops,
        buttonText: 'Close',
        type: ErrorModalTypes.GENERAL,
        onSubmit: () => {
          router.navigate({ to: routesModel[RoutesMap.WELCOME].path });
        },
      },
    });
    reportError({
      severity: ObservabilityErrorSeverity.ERROR,
      metadata: {
        name: 'An unknown error occurred',
        message: `Unmapped error code: ${errorCode}, localized error message: ${localizedErrorMessage}, rawHttpStatus: ${rawHttpStatus}`,
      },
      groupingHash: 'unknown-error-code',
    });
  }
  const errorObject = getCombinedErrorsObjectMap().get(errorCode);
  if (errorObject?.relatedFieldName) {
    setFormFieldError(
      errorObject.relatedFieldName,
      localizedErrorMessage ?? 'General error' //todo - in18 + text
    );
    reportFormErrorShown(errorObject.relatedFieldName, errorCode, localizedErrorMessage);
  }
  if (errorObject?.redirectPath || errorObject?.nonUserRedirectPath) {
    if (shouldActAsUsFlow && errorObject.redirectPath) {
      router.navigate({ to: errorObject.redirectPath });
    } else if (!shouldActAsUsFlow && errorObject.nonUserRedirectPath) {
      router.navigate({ to: errorObject.nonUserRedirectPath });
    } else if (errorObject.redirectPath) {
      router.navigate({ to: errorObject.redirectPath });
    }
  }
  if (errorObject?.callBack) errorObject.callBack();
  if (errorObject?.openModal) {
    setErrorModal({
      error: errorCode.toString(),
      modalProps: errorObject.modalProps,
    });
  }
  reportAdditionalAnalytics(errorCode);
};
const reportAdditionalAnalytics = (errorCode: number) => {
  if (errorCode === AuthErrorMap.TOKEN_EXPIRED.errorCode) {
    reportWillViewExpireToken(errorCode);
  }
};

export const isLiliError = (err: unknown): err is ServerErrorResponse => {
  return err instanceof AxiosError && typeof err.response?.data?.errors[0]?.code === 'number';
};

export const isSpecificLiliError = (
  err: unknown,
  errorCode: number
): err is ServerErrorResponse => {
  return isLiliError(err) && getLiliErrorCode(err) === errorCode;
};

export const getLiliErrorCode = (err: ServerErrorResponse) => {
  return err.response?.data?.errors[0]?.code;
};
